import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useCreateEpicRequest,
  useCreatePersonalEpicRequest,
} from "@/hooks/queries/use-epic-requests";
import { KEY_RUNNER, useIdeaHelperRunners, useStartIdeaHelper } from "./idea-helper-start";
import { cloudReasonText } from "@/components/epic-requests/run-with-choice";
import { useCloudRunStatus } from "@/hooks/queries/use-cloud-runs";
import { useTeams } from "@/hooks/queries/use-teams";
import { usePageTitle } from "@/hooks/use-page-title";
import { ApiError } from "@/lib/api/client";
import { cn } from "@/lib/utils";
import type { IdeaRunsOn } from "@mycompbox/shared";

/**
 * New idea (docs/new-look/PLAN.md, steps 3.1–3.2): say it in a line or two,
 * then either start the interview (the idea goes in as the first message) or
 * save it for later in a project.
 *
 * "Where this idea runs" (ER-145) is saved on the idea: a ready computer runs
 * the interview, blueprint and build there; CompBox cloud runs the interview
 * on the AI key and the blueprint and build as CompBox cloud runs.
 */


const EXAMPLES = [
  "Let shoppers get an email when a sold-out item is back in stock.",
  "Add a size guide to every product page.",
  "Email me a sales report every Monday morning.",
];

/** A short title from the idea: its first sentence, cut to about eight words. */
export function titleFrom(text: string): string {
  const firstSentence = text.trim().split(/(?<=[.!?])\s+/)[0] ?? "";
  const words = firstSentence
    .replace(/[.!?]+$/, "")
    .split(/\s+/)
    .filter(Boolean);
  const short = words.slice(0, 8).join(" ");
  return words.length > 8 ? `${short}…` : short;
}

/** The last project picked here, so the next idea starts in the same place. */
const PROJECT_KEY = "compbox-new-idea-project";

function lastProject(): string {
  try {
    return localStorage.getItem(PROJECT_KEY) ?? "";
  } catch {
    return "";
  }
}

function rememberProject(project: string): void {
  try {
    localStorage.setItem(PROJECT_KEY, project);
  } catch {
    // Storage blocked: the pick just isn't remembered.
  }
}

const FIELD =
  "rounded-xl border border-border bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function NewIdeaPage() {
  usePageTitle("New idea");
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [picked, setProject] = useState(lastProject);
  const [error, setError] = useState<string | null>(null);
  const { data: teams } = useTeams();
  const createTeam = useCreateEpicRequest();
  const createPersonal = useCreatePersonalEpicRequest();
  const projects = [...(teams?.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  // A remembered project that's gone (or not loaded yet) falls back to Personal.
  const project = projects.some((t) => t.id === picked) ? picked : "";
  const idea = text.trim();
  const saving = createTeam.isPending || createPersonal.isPending;

  // Where this idea runs: a ready computer, or CompBox cloud (the interview on
  // the AI key, then cloud runs). Cloud is listed but can't be picked while it
  // isn't available, with the reason why.
  const runners = useIdeaHelperRunners();
  const { data: cloudStatus } = useCloudRunStatus();
  const keyInterview = runners.some((r) => r.value === KEY_RUNNER);
  const cloudReason = !cloudStatus
    ? "Checking…"
    : (cloudReasonText(cloudStatus) ?? (keyInterview ? null : "The AI key can't run the interview here"));
  const places = [
    ...runners.filter((r) => r.value !== KEY_RUNNER).map((r) => ({ ...r, disabled: false })),
    {
      value: KEY_RUNNER,
      label: cloudReason ? `CompBox cloud (${cloudReason})` : "CompBox cloud",
      disabled: cloudReason !== null,
    },
  ];
  const usable = places.filter((p) => !p.disabled);
  const [runnerChoice, setRunnerChoice] = useState("");
  const runner = usable.some((r) => r.value === runnerChoice)
    ? runnerChoice
    : (usable[0]?.value ?? "");
  const runsOn: IdeaRunsOn | undefined = runner ? (runner === KEY_RUNNER ? "cloud" : "computer") : undefined;
  const startIdeaHelper = useStartIdeaHelper();
  const [starting, setStarting] = useState(false);

  const startInterview = async () => {
    if (!idea || !runner || starting) return;
    setError(null);
    setStarting(true);
    try {
      // Save the idea first, then talk it through on its own page's chat.
      const input = { title: titleFrom(idea), description: idea, ...(runsOn ? { runsOn } : {}) };
      const res = project
        ? await createTeam.mutateAsync({ ...input, teamId: project })
        : await createPersonal.mutateAsync(input);
      // If the Idea helper can't start, the idea is still saved: its page
      // offers "Discuss in the chat", so never save it twice from here.
      await startIdeaHelper({ epicRequestId: res.data.id, runner, firstMessage: idea }).catch(() => undefined);
      void navigate(`/ideas/${res.data.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't start the interview. Try again.");
      setStarting(false);
    }
  };

  const saveForLater = async () => {
    if (!idea || saving || starting) return;
    setError(null);
    const input = { title: titleFrom(idea), description: idea, ...(runsOn ? { runsOn } : {}) };
    try {
      const res = project
        ? await createTeam.mutateAsync({ ...input, teamId: project })
        : await createPersonal.mutateAsync(input);
      void navigate(`/ideas/${res.data.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't save the idea. Try again.");
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 py-2">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">New idea</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Say it in a line or two. The Idea helper then asks a few questions until it's clear enough
          to plan.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="new-idea-text" className="text-sm font-medium text-foreground">
          Your idea
        </label>
        <textarea
          id="new-idea-text"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
          }}
          rows={5}
          placeholder="What's your idea?"
          className={cn(
            FIELD,
            "resize-y p-4 text-base leading-relaxed shadow-[0_4px_16px_rgba(37,99,235,0.08)]"
          )}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Try:</span>
        {EXAMPLES.map((example) => (
          <button
            key={example}
            type="button"
            onClick={() => {
              setText(example);
            }}
            className="min-h-9 rounded-full border border-border bg-background px-3 text-left text-[13px] text-foreground/80 hover:bg-muted"
          >
            {example}
          </button>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
          Where this idea runs
          {usable.length > 0 ? (
            <select
              aria-label="Where this idea runs"
              value={runner}
              onChange={(e) => {
                setRunnerChoice(e.target.value);
              }}
              className={cn(FIELD, "min-h-11 px-3 text-sm font-normal")}
            >
              {places.map((r) => (
                <option key={r.value} value={r.value} disabled={r.disabled}>
                  {r.label}
                </option>
              ))}
            </select>
          ) : (
            <span className="rounded-xl border border-dashed border-border px-3 py-2.5 text-sm font-normal text-muted-foreground">
              No computer is online and CompBox cloud can't run it
              {cloudReason && cloudReason !== "Checking…" ? ` (${cloudReason})` : ""}.{" "}
              <Link to="/connect" className="font-medium text-primary">
                Connect a computer
              </Link>{" "}
              or add an Anthropic key in{" "}
              <Link to="/settings" className="font-medium text-primary">
                Settings
              </Link>
              .
            </span>
          )}
          {runsOn && (
            <span className="text-xs font-normal text-muted-foreground">
              {runsOn === "cloud"
                ? "The interview runs on your AI key; the blueprint and build run in CompBox cloud."
                : "The interview, blueprint and build run on your computer."}
            </span>
          )}
        </label>
        <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
          Project
          <select
            value={project}
            onChange={(e) => {
              setProject(e.target.value);
              rememberProject(e.target.value);
            }}
            className={cn(FIELD, "min-h-11 px-3 text-sm font-normal")}
          >
            <option value="">Personal (only you)</option>
            {projects.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <p
          role="alert"
          className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
        <Link
          to="/ideas"
          className="min-h-11 content-center text-sm text-muted-foreground hover:text-foreground"
        >
          Cancel
        </Link>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!idea || saving || starting}
            onClick={() => {
              void saveForLater();
            }}
            className="min-h-11 rounded-xl border border-border bg-background px-4 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save for later"}
          </button>
          <button
            type="button"
            disabled={!idea || !runner || starting || saving}
            onClick={() => {
              void startInterview();
            }}
            className="min-h-11 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {starting ? "Starting…" : "Start the interview"}
          </button>
        </div>
      </div>
    </div>
  );
}
